import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
  useSyncExternalStore, type ReactNode,
} from "react";

// App-level chrome: the persistent header (handoff §10b) reads a title,
// subtitle/meta line, an optional actions node, and an optional tabs node that
// each view pushes up. Plus a global search query (§10a) shared with views.
//
// The nodes live in a tiny external store the *header* subscribes to. A view
// republishes its chrome on every commit, which re-renders only the header (an
// external-store subscriber) and never the view itself — so stateful actions or
// tabs (e.g. a segmented toggle sitting in the header) stay in sync with no
// render loop and no need for callers to enumerate every value the nodes close
// over. This replaces the old deps-gated snapshot, which silently dropped JSX
// updates the deps array didn't mention (bug 2) and could blank the header when
// two views' cleanups raced on navigation (bug 3).

interface ChromeSnapshot {
  title: string;
  subtitle: string;
  actions: ReactNode;
  tabs: ReactNode;
  back?: { to: string; label: string };
}

const EMPTY: ChromeSnapshot = { title: "", subtitle: "", actions: null, tabs: null, back: undefined };

interface ChromeStore {
  getSnapshot: () => ChromeSnapshot;
  subscribe: (listener: () => void) => () => void;
  publish: (next: ChromeSnapshot) => void;
}

function sameSnapshot(a: ChromeSnapshot, b: ChromeSnapshot): boolean {
  return (
    a.title === b.title &&
    a.subtitle === b.subtitle &&
    a.actions === b.actions &&
    a.tabs === b.tabs &&
    a.back === b.back
  );
}

function createChromeStore(): ChromeStore {
  let snapshot: ChromeSnapshot = EMPTY;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    publish: (next) => {
      // Skip no-op publishes (views with no nodes re-committing) so the header
      // only re-renders when something the header shows actually changed. Views
      // that carry JSX nodes build new node objects each render, so those always
      // differ and the header stays fresh.
      if (sameSnapshot(snapshot, next)) return;
      snapshot = next;
      listeners.forEach((l) => l());
    },
  };
}

interface ChromeContextValue {
  store: ChromeStore;
  query: string;
  setQuery: (q: string) => void;
}

const ChromeContext = createContext<ChromeContextValue | null>(null);

export function ChromeProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<ChromeStore>();
  if (!storeRef.current) storeRef.current = createChromeStore();
  const [query, setQuery] = useState("");

  const value = useMemo<ChromeContextValue>(
    () => ({ store: storeRef.current!, query, setQuery }),
    [query]
  );

  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

export function useChromeContext(): ChromeContextValue {
  const ctx = useContext(ChromeContext);
  if (!ctx) throw new Error("useChromeContext must be used within ChromeProvider");
  return ctx;
}

/** The persistent header reads its chrome here — subscribing to the store, not
 *  to view state, so it updates whenever a view republishes. */
export function useChromeHeader(): ChromeSnapshot {
  const { store } = useChromeContext();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * Views call this ONCE per route to publish their title/subtitle/actions/tabs
 * into the persistent header. It republishes on every commit, so JSX actions
 * and tabs never go stale. The `deps` argument is accepted for backward
 * compatibility with existing call sites and is intentionally ignored.
 */
export function useChrome(
  chrome: { title: string; subtitle?: string; actions?: ReactNode; tabs?: ReactNode; back?: { to: string; label: string } },
  _deps: unknown[] = []
) {
  const { store } = useChromeContext();

  // Republish on every commit (no cleanup here, so there's no EMPTY flash
  // between a view's own re-renders).
  useEffect(() => {
    store.publish({
      title: chrome.title,
      subtitle: chrome.subtitle ?? "",
      actions: chrome.actions ?? null,
      tabs: chrome.tabs ?? null,
      back: chrome.back,
    });
  });

  // Clear only on unmount. On a route swap React runs the outgoing view's
  // cleanup before the incoming view's effects, so the header lands on the new
  // view's chrome instead of blanking.
  useEffect(() => () => store.publish(EMPTY), [store]);
}

/** Case-insensitive title filter used across list/board views (§10a). */
export function matchesQuery(title: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return title.toLowerCase().includes(q);
}

export function useSearchQuery(): string {
  return useChromeContext().query;
}
